/** Split a calendar event into a video join link and a physical place. */

export type VideoProviderId =
  | "google-meet"
  | "zoom"
  | "teams"
  | "webex"
  | "goto"
  | "slack"
  | "zoho"
  | "facetime"
  | "whereby"
  | "jitsi"
  | "chime"
  | "ringcentral"
  | "bluejeans"
  | "skype"
  | "discord"
  | "other";

export type VideoMeeting = {
  url: string;
  provider: VideoProviderId;
  label: string;
  /** ConnectionBrandIcon id when we have a mark. */
  brandId: string;
};

const PROVIDERS: { id: VideoProviderId; label: string; brandId: string; test: RegExp }[] = [
  { id: "google-meet", label: "Google Meet", brandId: "google", test: /meet\.google\.com|hangouts\.google\.com/i },
  { id: "zoom", label: "Zoom", brandId: "zoom", test: /(^|\.)zoom\.us|zoomgov\.com/i },
  { id: "teams", label: "Microsoft Teams", brandId: "teams", test: /teams\.microsoft\.com|teams\.live\.com/i },
  { id: "webex", label: "Webex", brandId: "webex", test: /webex\.com/i },
  { id: "goto", label: "GoTo Meeting", brandId: "goto", test: /gotomeeting\.com|gotomeet\.me|goto\.com/i },
  { id: "slack", label: "Slack", brandId: "slack", test: /slack\.com\/(huddle|call)|app\.slack\.com\/huddle/i },
  { id: "zoho", label: "Zoho Meeting", brandId: "zoho", test: /meeting\.zoho\.com/i },
  { id: "facetime", label: "FaceTime", brandId: "facetime", test: /facetime\.apple\.com/i },
  { id: "whereby", label: "Whereby", brandId: "whereby", test: /whereby\.com/i },
  { id: "jitsi", label: "Jitsi", brandId: "jitsi", test: /meet\.jit\.si|jitsi/i },
  { id: "chime", label: "Amazon Chime", brandId: "chime", test: /chime\.aws/i },
  { id: "ringcentral", label: "RingCentral", brandId: "ringcentral", test: /ringcentral\.com/i },
  { id: "bluejeans", label: "BlueJeans", brandId: "bluejeans", test: /bluejeans\.com/i },
  { id: "skype", label: "Skype", brandId: "skype", test: /join\.skype\.com|skype\.com/i },
  { id: "discord", label: "Discord", brandId: "discord", test: /discord\.gg|discord\.com/i },
];

const MAPS = /maps\.google\.|google\.[^/]+\/maps|maps\.apple\.com|maps\.app\.goo\.gl|goo\.gl\/maps|waze\.com|bing\.com\/maps|openstreetmap\.org/i;

export function classifyVideoUrl(raw: string): VideoMeeting | null {
  const url = normalizeUrl(raw);
  if (!url) return null;
  let host = "";
  try {
    host = new URL(url).hostname + new URL(url).pathname;
  } catch {
    return null;
  }
  if (MAPS.test(url)) return null;
  for (const provider of PROVIDERS) {
    if (provider.test.test(host) || provider.test.test(url)) {
      return { url, provider: provider.id, label: provider.label, brandId: provider.brandId };
    }
  }
  return null;
}

export function isMapUrl(raw: string): boolean {
  const url = normalizeUrl(raw);
  return Boolean(url && MAPS.test(url));
}

export function isHttpUrl(raw: string): boolean {
  return Boolean(normalizeUrl(raw));
}

function normalizeUrl(raw: string): string | null {
  const text = raw.trim().replace(/^<|>$/g, "");
  if (!text) return null;
  const withScheme = /^https?:\/\//i.test(text) ? text : /^[\w.-]+\.[a-z]{2,}(\/|$)/i.test(text) ? `https://${text}` : "";
  if (!withScheme) return null;
  try {
    const url = new URL(withScheme);
    if (url.protocol !== "http:" && url.protocol !== "https:") return null;
    return url.toString();
  } catch {
    return null;
  }
}

const URL_IN_TEXT = /https?:\/\/[^\s<>"']+/gi;

export function extractUrls(text: string): string[] {
  const found = text.match(URL_IN_TEXT) || [];
  const hrefs = [...text.matchAll(/href\s*=\s*["']([^"']+)["']/gi)].map((m) => m[1] || "");
  const urls: string[] = [];
  for (const raw of [...found, ...hrefs]) {
    const url = normalizeUrl(raw.replace(/[),.;]+$/, ""));
    if (url && !urls.includes(url)) urls.push(url);
  }
  return urls;
}

export type EventPlace = {
  meeting: VideoMeeting | null;
  /** Street, room, or map link. Video URLs are not a location. */
  location: string;
  locationHref: string | null;
};

export function resolveEventPlace(input: {
  meetLink?: string;
  hangoutLink?: string;
  conferenceLink?: string;
  location?: string;
  description?: string;
}): EventPlace {
  const explicit = [input.meetLink, input.hangoutLink, input.conferenceLink]
    .map((value) => (value ? classifyVideoUrl(value) : null))
    .find(Boolean) || null;

  const locationRaw = (input.location || "").trim();
  const locationUrls = extractUrls(locationRaw);
  const locationMeeting = locationUrls.map(classifyVideoUrl).find(Boolean) || null;
  const descriptionMeeting =
    extractUrls(input.description || "").map(classifyVideoUrl).find(Boolean) || null;

  const meeting = explicit || locationMeeting || descriptionMeeting || null;

  let location = locationRaw;
  for (const url of locationUrls) {
    if (classifyVideoUrl(url) || (meeting && url === meeting.url)) {
      location = location.replace(url, " ").replace(/https?:\/\/\S+/gi, (hit) => (classifyVideoUrl(hit) ? " " : hit));
    }
  }
  location = location.replace(/\s+/g, " ").replace(/^[\s,;|-]+|[\s,;|-]+$/g, "").trim();

  if (location && isHttpUrl(location) && !isMapUrl(location) && classifyVideoUrl(location)) {
    location = "";
  }

  const locationHref = location && (isHttpUrl(location) || isMapUrl(location)) ? normalizeUrl(location) : null;
  return { meeting, location, locationHref };
}

export function descriptionLooksLikeHtml(text: string): boolean {
  return /<\/?[a-z][\s\S]*>/i.test(text);
}

/** Keep calendar HTML readable and drop scripts and event handlers. */
export function sanitizeEventHtml(input: string): string {
  let html = input.trim();
  if (!descriptionLooksLikeHtml(html) && /&lt;\/?[a-z]/i.test(html)) {
    html = html
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&amp;/g, "&");
  }
  html = html.replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "");
  html = html.replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, "");
  html = html.replace(/<iframe[\s\S]*?>[\s\S]*?<\/iframe>/gi, "");
  html = html.replace(/<(object|embed|form)[\s\S]*?>[\s\S]*?<\/\1>/gi, "");
  html = html.replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, "");
  html = html.replace(/(href|src)\s*=\s*(['"])\s*javascript:[^'"]*\2/gi, '$1=$2#$2');
  return html;
}
